import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";

const INPUT = "bg-[#050505] border-[#2A2411] text-white";
const EMPTY = { current: "", next: "", confirm: "" };

const detailText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Could not change password";
};

const localError = ({ next, confirm }) => {
  if (next.length < 10) return "New password must be at least 10 characters";
  if (!/[a-z]/i.test(next) || !/\d/.test(next)) return "New password must contain letters and numbers";
  if (next !== confirm) return "New passwords do not match";
  return "";
};

export const ChangePasswordDialog = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const field = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const local = localError(form);
    if (local) return setError(local);
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next }, { withCredentials: true });
      toast.success("Password changed. Other sessions have been signed out.");
      setForm(EMPTY);
      setOpen(false);
      onChanged();
    } catch (err) {
      setError(detailText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setError(""); setForm(EMPTY); }}>
      <DialogTrigger asChild>
        <Button data-testid="admin-change-password-btn" variant="outline" className="border-[#2A2411] text-zinc-300">
          <KeyRound size={14} className="mr-1.5" /> Change password
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="admin-change-password-dialog" className="bg-[#0A0A0A] border-[#2A2411] text-white">
        <DialogHeader>
          <DialogTitle className="font-display">Change password</DialogTitle>
          <DialogDescription className="text-zinc-500">At least 10 characters with letters and numbers. Other signed-in sessions will be signed out.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="admin-current-password-input" type="password" autoComplete="current-password" placeholder="Current password" value={form.current} onChange={field("current")} className={INPUT} />
          <Input data-testid="admin-new-password-input" type="password" autoComplete="new-password" placeholder="New password" value={form.next} onChange={field("next")} className={INPUT} />
          <Input data-testid="admin-confirm-password-input" type="password" autoComplete="new-password" placeholder="Confirm new password" value={form.confirm} onChange={field("confirm")} className={INPUT} />
          {error && <p data-testid="admin-change-password-error" className="text-sm text-red-400">{error}</p>}
          <Button data-testid="admin-change-password-submit" type="submit" disabled={busy} className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Update password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
